import { Button, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Loader } from "../../components/loader/Loader";
import CartProductCard from "./CartProductCard";
import RenderSvg from "../renderSvg/RenderSvg";
import { Divider } from "@mantine/core";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import {
    getOutletDetails,
  getValidateRequestBody,
  updateQuantityInCart,
} from "../../features/createSecondaryOrder/CreateSecondaryOrderService";
import { ChannelkartNetworkPost } from "../../services/networkService";
import FormHeader from "../dms_common/FormHeader";
import { determineOrderType } from "../dms_common/useQuery";
import { DeliveryDetails } from "./DeliveryDetails";
import CartSummaryFooter from "../dms_common/CartSummaryFooter";
import { formatDate } from "./util";
import NewDiscountModalPrimary from "../../features/claimsNew/modals/NewDiscountModalPrimary";
import { getSkuData } from "./CartProductCardUtils";
import { useDispatch, useSelector } from "react-redux";
import { setDiscountModalState } from "../../stateManagement/actions/claims";
import { getDataFromLocalDb, insertDataIntoLocalDB } from "../../services/dbService";
import { Cart, Catalogue, CartProduct } from "../../features/createSecondaryOrder/models";
import {
  GenericModal,
  getGenericModalProps,
  initialGenericModalProps,
} from "../../features/viewOrder/ViewOrderUtils";
import CustomGenericModal from "../dms_common/CustomGenericModal";
import "./Checkout.css";
import { OrderType } from "../../types/types";
import ScreenOverlay from "../dms_common/ScreenOverlay";
import { setSecondaryState } from "../../stateManagement/actions/secondaryOrderActions";
import CartProductCardPrimary from "./CartProductCardPrimary";

const CheckoutPrimary = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const useQuery = () => {
    return new URLSearchParams(useLocation().search);
  };
  const query = useQuery();
  const outletId = query.get("outletId") || "null";
  const [cart, setCart] = useState<Cart>({});
  const [catalogue, setCatalogue] = useState<Catalogue[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [placeOrderLoading, setPlaceOrderLoading] = useState<boolean>(false);
  const [genericModalProps, setGenericModalProps] =
    useState<GenericModal>(initialGenericModalProps);
  const [skuData, setSkuData] = useState<any>({});

  const orderType = determineOrderType(location?.pathname);

  const dispatch = useDispatch();

  const cartProducts = cart?.[outletId]?.productsMap;
  const { createNewOrderState } = useSelector((state: any) => state.secondaryOrder);

  const handleCheckout = async () => {
    setPlaceOrderLoading(true);
    const customerData = cart?.[outletId]?.customer || {};

    const shippingAddress = customerData?.shippingAddress || "";
    const billDate = formatDate(customerData?.billDate) || "";
    const deliveryDate = formatDate(customerData?.deliveryDate) || "";
    const salesRep = customerData?.salesRep || "";
    const salesType = customerData?.salesType || "";
    const customerChannel = customerData?.customerChannel || "";
    const paymentMethod = customerData?.paymentMethod || "";
    const diffShippingAddress = createNewOrderState?.diffShippingAddress || false;

    const customerInfo = {
      billToAddress: shippingAddress,
      shipToAddress: cart?.[outletId]?.customer?.differentShippingAddress ?? "",
      billDate,
      deliveryDate,
      salesRep,
      salesType,
      customerChannel,
      paymentMethod,
      diffShippingAddress
    };

    try {
      const outletData = await getOutletDetails(JSON.parse(localStorage.authContext)?.user?.loginId);
      const requestBody = getValidateRequestBody(
        cart?.[outletId],
        undefined, undefined, undefined, undefined, undefined, undefined, undefined, // skip others
        outletData[0], true
      );
      const response = await ChannelkartNetworkPost(
        "/orders/validate?transformerOut=orderDetailSplit",
        requestBody
      );

      if (response?.status !== 200 && response?.status !== 201) {
        setGenericModalProps(
          getGenericModalProps("error", "There is some issue while validating cart")
        );
      } else {
        navigate(`summary?orderType=${orderType}&outletId=${outletId}`, {
          state: {
            orderValidateRes: response?.data?.feature,
            ...customerInfo,
            skuCodeSortOrder: response?.data?.feature?.[0]?.extendedAttributes?.skuCodeSortOrder || []
          },
        });
      }
    } catch (error) {
      setGenericModalProps(
        getGenericModalProps("error", "There is some issue while validating cart")
      );
      console.error(error);
    } finally {
      setPlaceOrderLoading(false);
    }
  };

  const resetCart = async () => {
    setCart((prevCart) => {
      const updatedCart = {
        ...prevCart,
        [outletId]: { ...prevCart[outletId], productsMap: {} },
      };
      dispatch(
        setSecondaryState(createNewOrderState, {
          ...createNewOrderState,
          diffShippingAddress: false,
        })
      );
      insertDataIntoLocalDB("Cart", [{ id: 1, data: updatedCart }]);
      return updatedCart;
    });
  };

  const handleStepperBack = () => {
    navigate(location.pathname.replace(/\/([^/]*)$/, `?outletId=${outletId}`));
  };

  useEffect(() => {
    const getAndSetData = async () => {
      setLoading(true);
      try {
        const responseCart = await getDataFromLocalDb("Cart");
        const batchCodes = [...Object.keys(responseCart?.data?.[0]?.data?.[outletId]?.productsMap)];
        const query = { batchCode: { $in: batchCodes } };
        const responseSelectedCatalogue = await getDataFromLocalDb(
          orderType === OrderType.SECONDARY ? "Catalogue" : "PrimaryCatalogue",
          query
        );
        setCatalogue(responseSelectedCatalogue?.data);
        setCart(responseCart?.data?.[0]?.data);
        setLoading(false);
      } catch (error) {
        setLoading(false);
        console.error(error);
      }
    };
    getAndSetData();
  }, []);

  const handleClickViewDetails = (product: CartProduct) => {
    setSkuData(getSkuData(product) ?? {});
    dispatch(setDiscountModalState(true));
  };

  const handleClickReset = () => {
    const updatedGenericModalProps = getGenericModalProps(
      `positive`,
      `Are you sure you want to reset the cart?`,
      `Confirm`,
      `Cancel`,
      async () => {
        await resetCart()
        setGenericModalProps(initialGenericModalProps)
      },
      () => setGenericModalProps(initialGenericModalProps),
    );
    setGenericModalProps(updatedGenericModalProps);
  }

  const itemsCount = Object.keys(cart?.[outletId]?.productsMap ?? {})?.length;

  useEffect(() => {
  const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 'a') {
        event.preventDefault();
        if (!loading && !placeOrderLoading && Object.keys(cart?.[outletId]?.productsMap ?? {}).length > 0) {
          handleCheckout();
        }
      } else if ((event.ctrlKey || event.metaKey) && event.key === 'z') {
        event.preventDefault();
        handleClickReset();
      } else if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
        event.preventDefault();
        handleStepperBack();
      }
  };

  window.addEventListener('keydown', handleKeyDown);
  return () => {
    window.removeEventListener('keydown', handleKeyDown);
  };
}, [loading, placeOrderLoading, cart, outletId, handleCheckout, handleClickReset, handleStepperBack]);

  return (
    <div className="checkout_container">
      <ScreenOverlay apply={loading || placeOrderLoading} />
      {loading ? (
        <Loader />
      ) : (
        <>
         <CustomGenericModal
            type={genericModalProps?.type}
            message={genericModalProps?.message}
            leftButtonText={genericModalProps?.leftButtonText}
            rightButtonText={genericModalProps?.rightButtonText}
            modalOpen={genericModalProps?.open}
            onLeftButtonClick={genericModalProps?.onLeftButtonClick}
            onRightButtonClick={genericModalProps?.onRightButtonClick}
            onClose={() => setGenericModalProps(initialGenericModalProps)}
          />

          <FormHeader
            parentLabel={"Place New Order"}
            label={"Check out"}
            goBackToParent={handleStepperBack}
          />
          <div className="checkout_grid_container">
            <div style={{ height: "95%", width: "58.5%" }}>
              <div>
                <div>
                  <div className="checkout_add_reset_total_product">
                    <span
                      style={{
                        fontSize: "16px",
                        color: "#227c9d",
                        fontWeight: 500,
                        marginLeft: "0.6rem",
                      }}
                    >
                      {itemsCount} {itemsCount > 1 ? "Products" : "Product"}
                    </span>
                    <div style={{ display: "flex", gap: "8px" }}>
                      <Button
                        color="primary"
                        style={{
                          border: "#2b82a1 solid 1px",
                          backgroundColor: "#ffffff",
                          fontSize: "10px",
                          fontWeight: 600,
                          textTransform: "none",
                          padding: "2px 8px",
                        }}
                        onClick={handleClickReset}
                      >
                        <div
                          style={{
                            height: "24px",
                            width: "19px",
                            paddingRight: "4px",
                          }}
                        >
                          <RenderSvg name={"backIcon"} />
                        </div>
                        Reset
                      </Button>
                      <Button
                        variant="contained"
                        color="primary"
                        style={{
                          fontSize: "11px",
                          padding: "0 11px",
                          fontWeight: 600,
                          textTransform: "none",
                        }}
                        sx={{
                          ":hover": {
                            boxShadow: "none",
                            background: "primary",
                          },
                          boxShadow: "none",
                        }}
                        onClick={() => {
                          handleStepperBack();
                        }}
                      >
                        <ShoppingCartIcon
                          style={{
                            height: "22px",
                            width: "14px",
                            marginRight: "4px",
                          }}
                        />
                        Add More
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
              <div className="product-list-grid">
                {Object.values(cart?.[outletId]?.productsMap ?? {})
                  .map((product, _, arr) => {
                    return (
                      <CartProductCardPrimary
                        key={product.batchCode}
                        product={product}
                        updateQuantityInCart={updateQuantityInCart}
                        orderType={orderType}
                        handleClickViewDetails={handleClickViewDetails}
                        totalProducts={arr?.length ?? 0}
                        resetCart={resetCart}
                        customerInfo={cart?.[outletId]?.customer}
                        setCart={setCart}
                        catalogue={catalogue}
                      />
                    );
                  })}
              </div>
            </div>
            <div className="checkout_customer_details_grid_container">
              <div className="customer_details_checkout">
                <div className="sub_header">
                  <div className="confirmed_order_icon">
                    <RenderSvg name="customerIcon" />
                  </div>
                  <Typography
                    sx={{ fontSize: "16px !important", color: "#227c9d", fontWeight: 500 }}
                  >{`${
                    orderType === OrderType.PRIMARY ? "Delivery" : "Customer"
                  } Details`}</Typography>
                </div>

                <Divider sx={{ height: "14px" }} color="#f1f1f1" />

                {orderType === OrderType.SECONDARY && (
                  <div className="checkout_summary_label_value_container">
                    <Typography className="label">{"Customer"}</Typography>
                    <Typography className="value">
                      {`${
                        cart?.[outletId]?.customer?.outletCode
                          ? cart?.[outletId]?.customer?.outletCode + " -"
                          : "-"
                      }  ${cart?.[outletId]?.customer?.outletName || ""}`}
                    </Typography>
                  </div>
                )}

                {orderType === OrderType.SECONDARY && (
                  <div className="checkout_summary_label_value_container">
                    <Typography className="label">{"Sales Rep"}</Typography>
                    <Typography className="value">
                      {cart?.[outletId]?.customer?.salesRep || "-"}
                    </Typography>
                  </div>
                )}
                {orderType === OrderType.PRIMARY ? (
                  <></>
                ) : (
                  <div className="checkout_summary_label_value_container">
                    <Typography className="label">{"Sales Type"}</Typography>
                    <Typography className="value">
                      {cart?.[outletId]?.customer?.salesType || "-"}
                    </Typography>
                  </div>
                )}
                {orderType === OrderType.SECONDARY && (
                  <div className="checkout_summary_label_value_container">
                    <Typography className="label">{"Sales Channel"}</Typography>
                    <Typography className="value">
                      {cart?.[outletId]?.customer?.customerChannel || "-"}
                    </Typography>
                  </div>
                )}
                <div className="checkout_summary_label_value_container">
                  <Typography className="label">{"Address"}</Typography>
                  <Typography className="value">
                    {cart?.[outletId]?.customer?.shippingAddress || "-"}
                  </Typography>
                </div>
                <div className="checkout_summary_label_value_container">
                  <Typography className="label">{"Bill Date"}</Typography>
                  <Typography className="value">
                    {formatDate(cart?.[outletId]?.customer?.billDate) || "-"}
                  </Typography>
                </div>
                <div className="checkout_summary_label_value_container">
                  <Typography className="label">{"Est. Delivery Date"}</Typography>
                  <Typography className="value">
                    {formatDate(cart?.[outletId]?.customer?.deliveryDate) || "-"}
                  </Typography>
                </div>

                <div className="checkout_summary_label_value_container">
                  <Typography className="label">{"Payment Method"}</Typography>
                  <Typography className="value">
                    {cart?.[outletId]?.customer?.paymentMethod || "-"}
                  </Typography>
                </div>
                <Divider sx={{ height: "15px" }} color="#fff" />

                <div className="checkout_delivery_details">
                  {orderType === OrderType.SECONDARY && (
                    <div className="sub_header">
                      <div className="confirmed_order_icon">
                        <RenderSvg name="customerIcon" />
                      </div>
                      <Typography
                        sx={{
                          fontSize: "16px !important",
                          color: "#227c9d",
                          fontWeight: 500,
                        }}
                      >
                        Delivery Details
                      </Typography>
                    </div>
                  )}
                  <DeliveryDetails
                    editedShippingAddress={
                      cart?.[outletId]?.customer?.differentShippingAddress ?? ""
                    }
                    cart={cart}
                    outletId={outletId}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="Checkout_cartSummaryFooter_container_wrapper">
            <div className="Checkout_cartSummaryFooter_container">
              <CartSummaryFooter
                isPrimary={true}
                cartProducts={cartProducts}
                onButtonClick={handleCheckout}
                buttonText={"Check Out"}
                loading={placeOrderLoading}
              />
            </div>
          </div>
        </>
      )}
      <NewDiscountModalPrimary
        selectedSkuCode={skuData?.productUI?.batchCode}
        skuData={skuData}
        showFooter={false}
        qtyLabel="In Stock"
        showMrpAndPackSize={true}
        createPrimaryOrder = {true}
      />
    </div>
  );
};

export default CheckoutPrimary;
